/**
 * Countdown block presentation schema (COUNTDOWN section.setting.display).
 * Keep in sync with client/utils/countdown-display.js
 */

export const COUNTDOWN_STOCK_TIMER_ENABLED = false;

export const COUNTDOWN_DISPLAY_LAYOUT = {
  BAR: "bar",
  INLINE: "inline",
  UNITS: "units",
};

export const COUNTDOWN_DISPLAY_STYLE = {
  PREMIUM_V1: "premium-v1",
  PREMIUM_V2: "premium-v2",
  PREMIUM_V3: "premium-v3",
};

export const DEFAULT_COUNTDOWN_DISPLAY = {
  layout: COUNTDOWN_DISPLAY_LAYOUT.UNITS,
  style: COUNTDOWN_DISPLAY_STYLE.PREMIUM_V1,
  urgencyText: "Hurry up! Offer expires in {{timer}}",
  showUrgencyText: true,
  backgroundColor: "#c0392b",
  textColor: "#ffffff",
  textFontSize: "14px",
  textFontWeight: "600",
};

export function normalizeCountdownStyle(raw) {
  if (!raw) return COUNTDOWN_DISPLAY_STYLE.PREMIUM_V1;
  const value = String(raw).toLowerCase();
  if (
    value === "countdown-v1" ||
    value === "design-1" ||
    value === "premium-v1"
  ) {
    return COUNTDOWN_DISPLAY_STYLE.PREMIUM_V1;
  }
  if (
    value === "countdown-v2" ||
    value === "design-2" ||
    value === "premium-v2"
  ) {
    return COUNTDOWN_DISPLAY_STYLE.PREMIUM_V2;
  }
  if (
    value === "countdown-v3" ||
    value === "design-3" ||
    value === "premium-v3"
  ) {
    return COUNTDOWN_DISPLAY_STYLE.PREMIUM_V3;
  }
  if (Object.values(COUNTDOWN_DISPLAY_STYLE).includes(value)) {
    return value;
  }
  return COUNTDOWN_DISPLAY_STYLE.PREMIUM_V1;
}

function legacyDisplayFromPopup(popupData = {}, section = {}) {
  const opts = popupData?.generalSettings?.layoutTree?.options || {};
  const legacyBar = opts.countdownBar || section?.setting?.countdownBar;
  const legacyVariant =
    section?.setting?.countdownVariant ||
    opts?.designVariants?.countdown;

  if (!legacyBar && !legacyVariant && !section?.setting?.combinedCountdownBar) {
    return {};
  }

  const style = normalizeCountdownStyle(legacyVariant);
  const fromBar = legacyBar
    ? {
        urgencyText: legacyBar.urgencyText,
        showUrgencyText: legacyBar.showUrgencyText,
        backgroundColor: legacyBar.backgroundColor,
        textColor: legacyBar.textColor,
        textFontSize: legacyBar.textFontSize,
        textFontWeight: legacyBar.textFontWeight,
      }
    : {};

  let layout;
  if (section?.setting?.combinedCountdownBar || legacyBar) {
    layout = COUNTDOWN_DISPLAY_LAYOUT.BAR;
  } else if (legacyVariant === "countdown-v1") {
    layout = COUNTDOWN_DISPLAY_LAYOUT.INLINE;
  }

  return {
    ...fromBar,
    ...(layout ? { layout } : {}),
    style,
  };
}

function displayFromCountdownTimer(timer) {
  if (!timer?.timer_type) return null;
  const block = timer.block_layout || {};
  const urgency = block.urgency_text || {};
  const STYLE_TO_DISPLAY = {
    pill: { layout: COUNTDOWN_DISPLAY_LAYOUT.BAR, style: "premium-v1", clockVariant: "pill" },
    blocks: { layout: COUNTDOWN_DISPLAY_LAYOUT.UNITS, style: "premium-v1", clockVariant: "blocks" },
    minimal: { layout: COUNTDOWN_DISPLAY_LAYOUT.UNITS, style: "premium-v3", clockVariant: "minimal" },
    flip: { layout: COUNTDOWN_DISPLAY_LAYOUT.UNITS, style: "premium-v2", clockVariant: "flip" },
    circular: { layout: COUNTDOWN_DISPLAY_LAYOUT.UNITS, style: "premium-v2", clockVariant: "circular" },
    bar: { layout: COUNTDOWN_DISPLAY_LAYOUT.INLINE, style: "premium-v1", clockVariant: "bar" },
  };
  const styleMap = STYLE_TO_DISPLAY[timer.display_style] || STYLE_TO_DISPLAY.pill;
  // Only emit fields that are explicitly set on the timer — never invent defaults
  // that would override merchant `setting.display` after the merge.
  const next = {
    layout: styleMap.layout,
    style: normalizeCountdownStyle(styleMap.style),
    clockVariant: styleMap.clockVariant,
    timeUnitsEnabled: timer.sections?.time_units?.enabled !== false,
    arrangement: block.arrangement || "space_between",
    stockMode: COUNTDOWN_STOCK_TIMER_ENABLED && timer.timer_type === "stock",
  };
  if (String(urgency.text ?? "").trim()) {
    next.urgencyText = urgency.text;
    next.showUrgencyText = urgency.enabled !== false;
  }
  if (block.background_color) next.backgroundColor = block.background_color;
  if (urgency.color) next.textColor = urgency.color;
  if (urgency.font_size) next.textFontSize = urgency.font_size;
  if (urgency.font_weight) next.textFontWeight = urgency.font_weight;
  if (timer.style?.digit?.color) next.digitColor = timer.style.digit.color;
  if (timer.style?.digit?.font_size) next.digitFontSize = timer.style.digit.font_size;
  return next;
}

export function resolveCountdownDisplay(section, popupData = {}) {
  const fromTimer = displayFromCountdownTimer(section?.setting?.countdown_timer);
  const stored = section?.setting?.display || {};
  const legacy = legacyDisplayFromPopup(popupData, section);

  // Merchant `setting.display` must win over timer block_layout / legacy defaults.
  const merged = {
    ...DEFAULT_COUNTDOWN_DISPLAY,
    ...(fromTimer || {}),
    ...legacy,
    ...stored,
  };

  const urgencyText = String(merged.urgencyText ?? "").trim();
  const layout =
    stored.layout ||
    fromTimer?.layout ||
    legacy.layout ||
    DEFAULT_COUNTDOWN_DISPLAY.layout;

  return {
    ...merged,
    style: normalizeCountdownStyle(
      stored.style || fromTimer?.style || legacy.style
    ),
    layout,
    urgencyBarEnabled:
      stored.urgencyBarEnabled ??
      legacy.urgencyBarEnabled ??
      (layout === COUNTDOWN_DISPLAY_LAYOUT.BAR || urgencyText.length > 0),
    showUrgencyText:
      merged.showUrgencyText !== false && urgencyText.length > 0
        ? true
        : merged.showUrgencyText === false
          ? false
          : urgencyText.length > 0,
  };
}

export function getUnitBlockStyle(timer) {
  const blocks = timer?.style?.blocks || timer?.style?.pill || {};
  return {
    backgroundColor: blocks.background_color || blocks.block_background || "rgba(255,255,255,0.18)",
    borderRadius: blocks.border_radius || "6px",
    padding: "4px 6px",
  };
}

export function getTimerPillStyleFromConfig(display, timer, isMobile = false) {
  const pill = timer?.style?.pill || {};
  const digit = timer?.style?.digit || {};
  const paddingMap = {
    compact: isMobile ? "2px 8px" : "3px 10px",
    spacious: isMobile ? "6px 14px" : "6px 16px",
    normal: isMobile ? "4px 12px" : "4px 14px",
  };
  const base = getTimerPillStyle(display, isMobile);
  return {
    ...base,
    backgroundColor: pill.background_color || base.backgroundColor,
    color: display?.digitColor || digit.color || base.color,
    borderRadius: pill.border_radius || base.borderRadius,
    fontSize: display?.digitFontSize || digit.font_size || base.fontSize,
    fontWeight: digit.font_weight || base.fontWeight,
    padding: paddingMap[pill.padding] || paddingMap.normal,
  };
}

export function getTimerPillStyle(display, isMobile = false) {
  const base = {
    fontSize: isMobile ? "18px" : "20px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    lineHeight: 1,
    borderStyle: "none",
    borderWidth: 0,
  };

  switch (normalizeCountdownStyle(display?.style)) {
    case COUNTDOWN_DISPLAY_STYLE.PREMIUM_V2:
      return {
        ...base,
        backgroundColor: "rgba(15,23,42,0.35)",
        color: "#ffffff",
        borderRadius: 999,
      };
    case COUNTDOWN_DISPLAY_STYLE.PREMIUM_V3:
      return {
        ...base,
        backgroundColor: "#ffffff",
        color: "#334155",
        borderRadius: 8,
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: "#e2e8f0",
      };
    case COUNTDOWN_DISPLAY_STYLE.PREMIUM_V1:
    default:
      return {
        ...base,
        backgroundColor: "rgba(255,255,255,0.22)",
        color: "#ffffff",
        borderRadius: 8,
      };
  }
}

function pxNumberSafe(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = parseFloat(String(value ?? "").replace(/px$/i, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

export function getCountdownBarShellCss(display, device = "desktop") {
  const isMobile = device === "mobile";
  const shell = {
    display: "flex",
    width: "100%",
    boxSizing: "border-box",
    justifyContent: "space-between",
    alignItems: "center",
    gap: isMobile ? 10 : 12,
    backgroundColor: display.backgroundColor || "#c0392b",
    color: display.textColor || "#ffffff",
    padding: isMobile ? "8px 12px" : "10px 16px",
  };
  // Never force borderRadius: 0 — that overwrote merchant Design tab saves.
  if (display.borderRadius != null && display.borderRadius !== "") {
    shell.borderRadius = display.borderRadius;
  }
  if (display.borderWidth != null && display.borderWidth !== "") {
    shell.borderWidth = display.borderWidth;
    shell.borderStyle =
      pxNumberSafe(display.borderWidth) > 0 ? "solid" : "none";
  }
  if (display.borderColor) shell.borderColor = display.borderColor;
  return shell;
}

export function applyCountdownBarShellToSection(section, display) {
  if (!section || display?.layout !== COUNTDOWN_DISPLAY_LAYOUT.BAR) return;
  section.setting = section.setting || {};
  section.setting.customCSS = section.setting.customCSS || {};
  const mergeDevice = (device) => {
    const existing = section.setting.customCSS[device] || {};
    const shell = getCountdownBarShellCss(display, device);
    const hidden =
      existing.display === "none" ? { display: "none" } : {};
    // Re-apply merchant border / title size after shell so design saves stick.
    return {
      ...existing,
      ...shell,
      ...(existing.borderRadius != null && existing.borderRadius !== ""
        ? { borderRadius: existing.borderRadius }
        : {}),
      ...(existing.borderWidth != null && existing.borderWidth !== ""
        ? {
            borderWidth: existing.borderWidth,
            borderStyle: existing.borderStyle || shell.borderStyle,
          }
        : {}),
      ...(existing.borderColor != null && existing.borderColor !== ""
        ? { borderColor: existing.borderColor }
        : {}),
      ...(existing.fontSize != null && existing.fontSize !== ""
        ? { fontSize: existing.fontSize }
        : {}),
      ...hidden,
    };
  };
  section.setting.customCSS.desktop = mergeDevice("desktop");
  section.setting.customCSS.mobile = mergeDevice("mobile");
}

export function usesMergedUrgencyBar(display) {
  return display?.layout === COUNTDOWN_DISPLAY_LAYOUT.BAR;
}

export function parseCountdownFixedEndDate(value) {
  if (value == null || String(value).trim() === "") return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

/** Prefer countdown_timer on the section so preview tracks editor before RHF flushes. */
export function resolveCountdownRuntime(popupData, section) {
  const timer = section?.setting?.countdown_timer;
  const condition = popupData?.generalSettings?.condition?.countdown || {};
  const values = { ...(condition.values || {}) };
  const conditions = { ...(condition.conditions || {}) };

  let countdown_type = condition.countdown_type || "moving_date";
  const timerType = timer?.timer_type;
  if (timerType === "fixed") {
    countdown_type = "till_fixed_date";
    if (timer?.fixed_options?.end_datetime) {
      values.countdownEndTime = timer.fixed_options.end_datetime;
    }
  } else if (timerType === "session") {
    countdown_type = "moving_date";
    const d = timer?.session_options?.duration || {};
    values.days = Number(d.days) || 0;
    values.hours = Number(d.hours) || 0;
    values.minutes = Number(d.minutes) || 0;
    values.seconds = Number(d.seconds) || 0;
  }

  const timeUnitsOn =
    displayFromCountdownTimer(timer)?.timeUnitsEnabled !== false &&
    timer?.sections?.time_units?.enabled !== false;

  if (!timeUnitsOn) {
    conditions.isHideDays = true;
    conditions.isHideHours = true;
    conditions.isHideMinutes = true;
    conditions.isHideseconds = true;
    conditions.isShowUnitLabels = false;
  } else {
    const units = timer?.units || {};
    conditions.isHideDays = units.days?.show === false;
    conditions.isHideHours = units.hours?.show === false;
    conditions.isHideMinutes = units.minutes?.show === false;
    conditions.isHideseconds = units.seconds?.show === false;
    conditions.isShowUnitLabels = Object.values(units).some(
      (u) => u?.show !== false && String(u?.label ?? "").trim() !== ""
    );
    values.dayText = units.days?.label ?? "";
    values.hourText = units.hours?.label ?? "";
    values.minuteText = units.minutes?.label ?? "";
    values.secondText = units.seconds?.label ?? "";
    conditions.isHideZeroUnits = timer?.show_units_if_zero !== true;
  }

  return { countdown_type, values, conditions, timeUnitsOn };
}

export function shouldRenderCountdownUnits(display, conditions, timeUnitsOn) {
  if (!timeUnitsOn || display?.timeUnitsEnabled === false) return false;
  const { isHideDays, isHideHours, isHideMinutes, isHideseconds } = conditions || {};
  return !(isHideDays && isHideHours && isHideMinutes && isHideseconds);
}

function padCountdown2(n) {
  return String(n).padStart(2, "0");
}

/** Pill text for urgency bar — respects visible units (no 99:00 cap). */
export function formatPillCountdown(timeLeft, conditions = {}, options = {}) {
  const { isHideDays, isHideHours, isHideMinutes, isHideseconds } = conditions;
  const d = Number(timeLeft.days) || 0;
  const h = Number(timeLeft.hours) || 0;
  const m = Number(timeLeft.minutes) || 0;
  const s = Number(timeLeft.seconds) || 0;

  if (options.compactWhenUnitsOff) {
    const totalSec = Math.max(0, d * 86400 + h * 3600 + m * 60 + s);
    const totalH = Math.floor(totalSec / 3600);
    const remM = Math.floor((totalSec % 3600) / 60);
    const remS = totalSec % 60;
    if (totalH > 0) {
      return `${padCountdown2(totalH)}:${padCountdown2(remM)}:${padCountdown2(remS)}`;
    }
    return `${padCountdown2(remM)}:${padCountdown2(remS)}`;
  }

  if (!isHideDays && d > 0) {
    return `${d}d ${padCountdown2(h)}:${padCountdown2(m)}:${padCountdown2(s)}`;
  }
  const totalH = (!isHideDays ? d * 24 : 0) + h;
  if (!isHideHours && totalH > 0) {
    return `${padCountdown2(totalH)}:${padCountdown2(m)}:${padCountdown2(s)}`;
  }
  if (!isHideMinutes) {
    return `${padCountdown2(m)}:${padCountdown2(s)}`;
  }
  if (!isHideseconds) {
    return `${padCountdown2(s)}s`;
  }
  return `${padCountdown2(m)}:${padCountdown2(s)}`;
}
